import { CircleCheck, Info, type LucideIcon, ShieldCheck, Sparkles, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '../lib/cn';

/**
 * Blok AI (DESIGN §5): `accent` — propozycje AI na accent/subtle, `subtle` — wynik pracy AI na bg/surface.
 * AI zawsze widoczne jako AI: ikona sparkles + etykieta.
 */
export function AIBlock({
  tone = 'accent',
  title,
  children,
  actions,
  footnote,
  className,
}: {
  tone?: 'accent' | 'subtle';
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  footnote?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('rounded-xl p-4', tone === 'accent' ? 'bg-accent-subtle' : 'bg-surface', className)}>
      <div className="flex items-center gap-2">
        <Sparkles size={16} strokeWidth={1.75} aria-hidden className="text-accent" />
        <h3 className="text-body-s font-semibold text-fg">{title}</h3>
      </div>
      {children && <div className="mt-2 text-body-s text-fg-secondary">{children}</div>}
      {actions && <div className="mt-4 flex flex-wrap items-center gap-2">{actions}</div>}
      {footnote && <p className="mt-3 text-caption text-fg-muted">{footnote}</p>}
    </section>
  );
}

const BANNER_ICON: Record<'accent' | 'warning' | 'info', LucideIcon> = {
  accent: ShieldCheck,
  warning: TriangleAlert,
  info: Info,
};

/**
 * Baner kontekstowy (DESIGN §5): jedna linia na accent/subtle (coś czeka na akceptację)
 * albo status/warning-subtle (wkrótce termin), link „Przejdź” po prawej. `info`: biały z ramką (bg/panel).
 */
export function ContextBanner({
  tone = 'accent',
  icon,
  children,
  linkTo,
  linkLabel = 'Przejdź',
  className,
}: {
  tone?: 'accent' | 'warning' | 'info';
  icon?: LucideIcon;
  children: ReactNode;
  linkTo?: string;
  linkLabel?: string;
  className?: string;
}) {
  const Icon = icon ?? BANNER_ICON[tone];
  return (
    <div
      role={tone === 'warning' ? 'alert' : 'status'}
      className={cn(
        'flex min-h-10 items-center gap-3 rounded-lg px-4 py-2 text-body-s text-fg',
        tone === 'accent' && 'bg-accent-subtle',
        tone === 'warning' && 'bg-warning-subtle',
        // info: biały baner z ramką — widoczny na szarym tle aplikacji i w białej sekcji (DECISIONS 2026-10-07)
        tone === 'info' && 'border border-line bg-panel',
        className,
      )}
    >
      <Icon
        size={16}
        strokeWidth={1.75}
        aria-hidden
        className={cn(
          'shrink-0',
          tone === 'accent' && 'text-accent',
          tone === 'warning' && 'text-warning',
          tone === 'info' && 'text-fg-muted',
        )}
      />
      <div className="min-w-0 flex-1">{children}</div>
      {linkTo && (
        <Link
          to={linkTo}
          className="shrink-0 rounded-xs font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
        >
          {linkLabel}
        </Link>
      )}
    </div>
  );
}

/** Komunikat w formularzu (błąd albo sukces). */
export function FormMessage({ tone = 'danger', children }: { tone?: 'danger' | 'success'; children: ReactNode }) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-2 rounded-lg px-3 py-2.5 text-body-s',
        tone === 'danger' ? 'bg-danger-subtle text-fg' : 'bg-success-subtle text-fg',
      )}
    >
      {tone === 'danger' ? (
        <TriangleAlert size={16} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-danger" />
      ) : (
        <CircleCheck size={16} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-success" />
      )}
      <span>{children}</span>
    </div>
  );
}
